import { Component, OnDestroy, ViewChild } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import notify from 'devextreme/ui/notify';
import { Subscription } from 'rxjs';
import {
  KalenderCreateTerminViewModel,
  KalenderDeleteTerminViewModel,
  KalenderTimedRange,
  KalenderService,
  KalenderUpdateTerminViewModel,
  toApiDateInKalenderTimeZone,
  toKalenderTimedRange,
  toValidKalenderDate,
} from 'src/app/department/department-start/startSite-Kalender/kalender.service';
import { PopupService } from '../../services/popup.service';

@Component({
  selector: 'app-kalenderTerminPopup',
  templateUrl: './kalenderTerminPopup.component.html',
  styleUrls: ['./kalenderTerminPopup.component.scss'],
  standalone: false,
})
export class KalenderTerminPopupComponent implements OnDestroy {
  @ViewChild('popup', { static: false }) popup: DxPopupComponent;
  @ViewChild('form', { static: false }) form: DxFormComponent;

  // Rückmeldung an StartSite-Kalender 
  onClose: (e) => void;
  formData: any = this.createEmptyFormData();
  // Rohdaten, mit denen das Popup geöffnet wurde.
  contextData: any;
  subscription_get_PopupAsObservable: Subscription;
  isLoading: boolean = false;
  // DevExtreme-Optionen für Datumseditor
  startDateEditorOptions: any = this.createDateEditorOptions('startDate');
  endDateEditorOptions: any = this.createDateEditorOptions('endDate');
  // Verhindert Rückkopplungen, wenn Datumswerte programmatisch gesetzt werden
  private suppressDateEditorEvents = false;
  // Endzeit "automatisch gesetzt" oder "manuell vom Nutzer geändert".
  private lastAutoEndDate: Date | null = null;
  // Uhrzeitkontext für Termin Popup - aus bestehenden Termin oder aus Zell-Auswahl
  private timedRange: KalenderTimedRange | null = null;

  // neuer oder vorhandener Termin
  get isNew(): boolean {
    return !(this.formData?.id && this.formData.id.toString().trim());
  }

  get popupTitle(): string {
    return this.isNew ? 'Termin anlegen' : 'Termin bearbeiten';
  }

  // Anzeigeformat der Datumsfelder
  get dateDisplayFormat(): string {
    return this.formData?.allDay ? 'dd.MM.yyyy' : 'dd.MM.yyyy   |   HH:mm';
  }

  constructor(private popupService: PopupService, private kalenderService: KalenderService) {
    this.onClickClose = this.onClickClose.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.onClickDelete = this.onClickDelete.bind(this);
    this.onAllDayValueChanged = this.onAllDayValueChanged.bind(this);
    this.onStartDateValueChanged = this.onStartDateValueChanged.bind(this);
    this.onEndDateValueChanged = this.onEndDateValueChanged.bind(this);
    this.onPopupHidden = this.onPopupHidden.bind(this);

    // Rohdaten und Uhrzeitkontext in Termin Popup übernehmen und anzeigen
    this.subscription_get_PopupAsObservable = this.popupService.get_kalenderTerminPopupAsObservable.subscribe((context) => {
      if (context !== null && context !== undefined && context.open === true) {
        this.onClose = context.closeCb;
        this.contextData = context.data;
        this.timedRange = this.getContextTimedRange(context.data);
        this.formData = this.normalizeFormData(context.data, this.timedRange);
        this.lastAutoEndDate = null;
        this.updateDateEditorOptions();
        this.popup.instance.show();
      } else {
        this.contextData = null;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription_get_PopupAsObservable) {
      this.subscription_get_PopupAsObservable.unsubscribe();
    }
  }

  // Schließt Termin Popup ohne Datenänderung
  onClickClose(e): void {
    this.isLoading = false;
    if (this.onClose !== undefined && this.onClose !== null) {
      this.onClose(3);
    }
    this.popup.instance.hide();
    this.resetState();
  }

  onPopupHidden(e): void {
    if (!this.contextData) {
      return;
    }

    this.isLoading = false;
    if (this.onClose !== undefined && this.onClose !== null) {
      this.onClose(3);
    }

    this.resetState();
  }

  // Datumslogik und Form validieren
  // zwischen Neu- und Bestand unterscheiden
  onClickSave(e): void {
    const validationResult = this.form.instance.validate();
    if (!validationResult.isValid || !this.validateDateRange()) {
      return;
    }

    this.isLoading = true;

    if (this.isNew) {
      const payload: KalenderCreateTerminViewModel = {
        text: (this.formData.text ?? '').trim(),
        description: this.formData.description?.trim() ? this.formData.description.trim() : null,
        startDate: toApiDateInKalenderTimeZone(this.formData.startDate, !!this.formData.allDay),
        endDate: toApiDateInKalenderTimeZone(this.formData.endDate, !!this.formData.allDay),
        allDay: !!this.formData.allDay,
        location: this.formData.location?.trim() ? this.formData.location.trim() : null,
      };
      this.kalenderService.addTermin(payload).subscribe({
        next: () => {
          this.isLoading = false;
          notify({ message: 'Termin erstellt.' }, 'success');
          if (this.onClose !== undefined && this.onClose !== null) {
            this.onClose(1);
          }
          this.popup.instance.hide();
          this.resetState();
        },
        error: (err) => {
          this.isLoading = false;
          notify({ message: err?.error?.message ?? err?.message ?? 'Fehler beim Erstellen.' }, 'error');
        },
      });
      return;
    }

    const payload: KalenderUpdateTerminViewModel = {
      id: this.formData.id.toString(),
      text: (this.formData.text ?? '').trim(),
      description: this.formData.description?.trim() ? this.formData.description.trim() : null,
      startDate: toApiDateInKalenderTimeZone(this.formData.startDate, !!this.formData.allDay),
      endDate: toApiDateInKalenderTimeZone(this.formData.endDate, !!this.formData.allDay),
      allDay: !!this.formData.allDay,
      location: this.formData.location?.trim() ? this.formData.location.trim() : null,
    };
    this.kalenderService.updateTermin(payload).subscribe({
      next: () => {
        this.isLoading = false;
        notify({ message: 'Termin aktualisiert.' }, 'success');
        if (this.onClose !== undefined && this.onClose !== null) {
          this.onClose(1);
        }
        this.popup.instance.hide();
        this.resetState();
      },
      error: (err) => {
        this.isLoading = false;
        notify({ message: err?.error?.message ?? err?.message ?? 'Fehler beim Aktualisieren.' }, 'error');
      },
    });
  }

  onClickDelete(e): void {
    if (this.isNew) {
      return;
    }

    const payload: KalenderDeleteTerminViewModel = {
      id: this.formData.id.toString(),
    };
    this.isLoading = true;

    this.kalenderService.deleteTermin(payload).subscribe({
      next: () => {
        this.isLoading = false;
        notify({ message: 'Termin gelöscht.' }, 'success');
        if (this.onClose !== undefined && this.onClose !== null) {
          this.onClose(1);
        }
        this.popup.instance.hide();
        this.resetState();
      },
      error: (err) => {
        this.isLoading = false;
        notify({ message: err?.error?.message ?? err?.message ?? 'Fehler beim Löschen.' }, 'error');
      },
    });
  }

  // Reagiert auf die Checkbox `Ganztägig`
  // je nach Zustand die Datumswerte umformen
  onAllDayValueChanged(e): void {
    this.formData.allDay = !!e.value;

    if (this.formData.allDay) {
      this.applyAllDayDateValues();
    } else {
      this.applyTimedDateValues();
    }

    this.form?.instance.updateData('allDay', this.formData.allDay);
    this.updateDateEditorOptions();
  }

  // Reagiert auf Änderungen am Startdatum
  // Endzeit wird dynamisch gesetzt
  onStartDateValueChanged(e): void {
    const startDate = toValidKalenderDate(e?.value);
    this.formData.startDate = startDate ?? e?.value;

    // Endzeit nicht ändern wenn: Termin Ganztag oder eigene Werte
    if (this.suppressDateEditorEvents || this.formData?.allDay || !startDate) {
      return;
    }

    const currentEndDate = toValidKalenderDate(this.formData.endDate);
    if (!this.shouldAutoUpdateEndDate(currentEndDate, startDate)) {
      return;
    }

    const endDate = this.getAutoEndDate(startDate);
    this.setDateValues(startDate, endDate);
    this.lastAutoEndDate = endDate;
  }

  // Reagiert auf Änderungen am Enddatum
  // Unterscheidung ob Endzeit automatisch erzeugt oder vom Nutzer gesetzt
  onEndDateValueChanged(e): void {
    const endDate = toValidKalenderDate(e?.value);
    this.formData.endDate = endDate ?? e?.value;

    if (this.suppressDateEditorEvents) {
      return;
    }

    if (!this.isSameDateTime(endDate, this.lastAutoEndDate)) {
      this.lastAutoEndDate = null;
    }
  }

  // Zusätzliche Datumsvalidierung 
  private validateDateRange(): boolean {
    const startDate = toValidKalenderDate(this.formData.startDate);
    if (!startDate) {
      notify({ message: 'Startdatum ist ungültig.' }, 'error');
      return false;
    }

    const endDate = toValidKalenderDate(this.formData.endDate);
    if (!endDate) {
      notify({ message: 'Enddatum ist ungültig.' }, 'error');
      return false;
    }

    const timedRange = toKalenderTimedRange(startDate, endDate);
    if (!timedRange) {
      notify({ message: 'Enddatum darf nicht vor dem Startdatum liegen.' }, 'error');
      return false;
    }

    this.formData.startDate = timedRange.startDate;
    this.formData.endDate = timedRange.endDate;

    return true;
  }

  // Mapping für Formular
  private normalizeFormData(data: any, timedRange: KalenderTimedRange | null): any {
    const empty = this.createEmptyFormData();
    const isNewTermin = !(data?.id ?? data?.Id);
    const rawStartDate = toValidKalenderDate(data?.startDate ?? null);
    const rawEndDate = toValidKalenderDate(data?.endDate ?? null);
    const startDate = timedRange?.startDate
      ? new Date(timedRange.startDate)
      : (isNewTermin
        ? (this.toStartOfDay(rawStartDate ?? empty.startDate) ?? empty.startDate)
        : (rawStartDate ?? empty.startDate));
    const endDate = timedRange?.endDate
      ? new Date(timedRange.endDate)
      : (isNewTermin
        ? (this.toStartOfDay(rawEndDate ?? startDate) ?? startDate)
        : (rawEndDate ?? new Date(startDate.getTime() + 60 * 60 * 1000)));

    return {
      id: data?.id ?? data?.Id ?? null,
      text: data?.text ?? data?.subject ?? '',
      description: data?.description ?? null,
      startDate,
      endDate,
      allDay: isNewTermin ? !timedRange : !!data?.allDay,
      location: data?.location ?? null,
    };
  }

  private createEmptyFormData(): any {
    const startDate = this.toStartOfDay(new Date()) ?? new Date();
    const endDate = new Date(startDate);

    return {
      id: null,
      text: '',
      description: null,
      startDate,
      endDate,
      allDay: true,
      location: null,
    };
  }

  // Räumt Popup-Zustand auf
  private resetState(): void {
    this.contextData = null;
    this.formData = this.createEmptyFormData();
    this.lastAutoEndDate = null;
    this.timedRange = null;
    this.updateDateEditorOptions();
  }

  // Baut Optionen für Datumseditor
  // `type`: `date` für Ganztag - `datetime` für Uhrzeit-Termine
  private createDateEditorOptions(field: 'startDate' | 'endDate'): any {
    return {
      type: this.formData?.allDay ? 'date' : 'datetime',
      stylingMode: 'outlined',
      displayFormat: this.dateDisplayFormat,
      useMaskBehavior: true,
      onValueChanged: field === 'startDate' ? this.onStartDateValueChanged : this.onEndDateValueChanged,
    };
  }

  // Aktualisiert beide Datumseditoren 
  private updateDateEditorOptions(): void {
    this.startDateEditorOptions = this.createDateEditorOptions('startDate');
    this.endDateEditorOptions = this.createDateEditorOptions('endDate');
  }

  // Hilfsfunktion: setzt Ganztagstermine auf Tagesbeginn zurück
  private toStartOfDay(value: string | Date | null | undefined): Date | null {
    const date = toValidKalenderDate(value);
    if (!date) {
      return null;
    }

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    return normalizedDate;
  }

  private addMinutes(value: string | Date | null | undefined, minutes: number): Date | null {
    const date = toValidKalenderDate(value);
    if (!date) {
      return null;
    }

    return new Date(date.getTime() + minutes * 60 * 1000);
  }

  // Startzeit + 30 Minuten = Endzeit
  private getAutoEndDate(startDate: string | Date | null | undefined): Date | null {
    return this.addMinutes(startDate, 30);
  }

  // Gibt den aktuellen Uhrzeitkontext zurück
  private getTimedRange(): KalenderTimedRange | null {
    if (!this.timedRange) {
      return null;
    }

    if (this.timedRange.endDate < this.timedRange.startDate) {
      return null;
    }

    return {
      startDate: new Date(this.timedRange.startDate),
      endDate: new Date(this.timedRange.endDate),
    };
  }

  // Entscheidet, ob das End Uhrzeit bei einer Startzeit-Änderung automatisch gesetzt wird
  private shouldAutoUpdateEndDate(endDate: Date | null, startDate: Date): boolean {
    if (!endDate) {
      return true;
    }

    if (endDate < startDate) {
      return true;
    }

    return this.isSameDateTime(endDate, this.lastAutoEndDate);
  }

  // Setzt Start und Ende zentral 
  private setDateValues(startDate: Date | null, endDate: Date | null): void {
    this.formData.startDate = startDate;
    this.formData.endDate = endDate;

    if (!this.form?.instance) {
      return;
    }

    this.suppressDateEditorEvents = true;
    try {
      this.form.instance.updateData('startDate', startDate);
      this.form.instance.updateData('endDate', endDate);
    } finally {
      this.suppressDateEditorEvents = false;
    }
  }

  // Vergleicht zwei Datum-Uhrzeit-Werte exakt
  private isSameDateTime(left: Date | null, right: Date | null): boolean {
    if (!left && !right) {
      return true;
    }

    if (!left || !right) {
      return false;
    }

    return left.getTime() === right.getTime();
  }

  // Liest Uhrzeitkontext aus Termin Popup
  private getContextTimedRange(data: any): KalenderTimedRange | null {
    return toKalenderTimedRange(data?.timedRange?.startDate ?? null, data?.timedRange?.endDate ?? null);
  }

  // Schaltet Formular von Uhrzeit auf Ganztag um
  //
  // Ablauf:
  // 1. aktuellen Uhrzeitbereich merken
  // 2. Start und Ende auf Tagesbeginn normalisieren
  // 3. Auto-Ende zurücksetzen
  private applyAllDayDateValues(): void {
    this.rememberTimedRange();
    const startDate = this.toStartOfDay(this.formData.startDate);
    const endDate = this.toStartOfDay(this.formData.endDate) ?? startDate;
    this.setDateValues(startDate, endDate);
    this.lastAutoEndDate = null;
  }

  // Schaltet das Formular von Ganztag auf Uhrzeit um.
  //
  // Ablauf:
  // 1. Falls existiert, gemerkten Uhrzeitbereich wiederherstellen
  // 2. Sonst lokaler Fallback `08:00 -> 08:30` 
  // 3. automatisch gesetztes Ende merken
  private applyTimedDateValues(): void {
    const timedRange = this.getTimedRange();
    if (timedRange) {
      this.setDateValues(timedRange.startDate, timedRange.endDate);
      this.lastAutoEndDate = null;
      return;
    }

    const baseDate = this.toStartOfDay(this.formData.startDate);
    const startDate = this.addMinutes(baseDate, 8 * 60);
    const endDate = this.getAutoEndDate(startDate);
    this.setDateValues(startDate, endDate);
    this.lastAutoEndDate = endDate;
  }

  // Merkt sich aktuellen Uhrzeitbereich nur, wenn Start und Ende vollständig und logisch sind
  private rememberTimedRange(): void {
    const timedRange = toKalenderTimedRange(this.formData.startDate, this.formData.endDate);
    if (!timedRange) {
      return;
    }

    this.timedRange = {
      startDate: new Date(timedRange.startDate),
      endDate: new Date(timedRange.endDate),
    };
  }

}
